import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import CustomCheckBox from '../CustomCheckBox';
import getGridWidthClass from '../../utils/getGridWidthClass';

export interface StudyTableCell {
  key?: string;
  content?: React.ReactNode;
  title?: string;
  gridCol: number;
}

export interface StudyData {
  isChecked?: boolean;
  [key: string]: any;
}

export interface TableData {
  reportId?: string;
  row: StudyTableCell[];
  expandedContent?: React.ReactNode;
  onClickRow?: (event: React.MouseEvent<HTMLTableRowElement>) => void;
  isExpanded?: boolean;
  dataCY?: string;
  studyData?: StudyData;
  toggleChecked?: (event: React.MouseEvent<HTMLInputElement>) => void;
  [key: string]: any;
}

export interface StudyListTableRowProps {
  tableData: TableData;
}

const CELL_STYLE: React.CSSProperties = {
  maxWidth: 0,
};

const LAST_CELL_STYLE: React.CSSProperties = {
  position: 'relative',
  padding: 0,
};

const stopPropagation = (event: React.MouseEvent) => {
  event.stopPropagation();
};

const StudyListTableRow: React.FC<StudyListTableRowProps> = ({ tableData }) => {
  const {
    row = [],
    expandedContent,
    onClickRow,
    isExpanded = false,
    dataCY,
    studyData,
    toggleChecked,
  } = tableData || {};
  const { t } = useTranslation();

  return (
    <tr
      className="select-none"
      data-cy={dataCY}
    >
      <td
        className={classnames('border-0 p-0', {
          'border-gray-800 bg-primary-dark border-b': isExpanded,
        })}
      >
        <div className="w-full transition duration-300 flex items-end">
          <table className="w-full p-4">
            <tbody>
              <tr
                className="bg-primary-dark hover:bg-secondary-main cursor-pointer transition duration-300 h-full"
                style={{ height: '100%' }}
                onClick={onClickRow}
              >
                {row.map((cell, index) => {
                  const { content, title, gridCol, key } = cell;
                  const isActionCell = key === 'instances';
                  return (
                    <td
                      key={key || index}
                      className={classnames(
                        'text-base',
                        isActionCell ? 'p-0 relative' : 'truncate px-4 py-2',
                        {
                          'border-gray-800 border-b': !isExpanded,
                        },
                        getGridWidthClass(gridCol)
                      )}
                      style={isActionCell ? LAST_CELL_STYLE : CELL_STYLE}
                      title={title}
                      onClick={isActionCell ? stopPropagation : undefined}
                    >
                      {isActionCell ? (
                        <>
                          <div
                            className="invisible opacity-0 pointer-events-none flex items-center justify-center px-4 py-2"
                            aria-hidden="true"
                          >
                            {content}
                          </div>
                          <div className="absolute inset-0 w-full h-full flex items-center justify-center">
                            {React.isValidElement(content)
                              ? React.cloneElement(content as React.ReactElement<any>, {
                                  style: {
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    ...((content.props as any)?.style || {}),
                                  },
                                  className: classnames(
                                    'w-full h-full flex items-center justify-center',
                                    (content.props as any)?.className
                                  ),
                                })
                              : content}
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center">
                          {index === 0 && (
                            <CustomCheckBox
                              className="mr-4 my-2"
                              checked={studyData?.isChecked ?? false}
                              onClick={toggleChecked}
                              ariaLabel={t('StudyList:Check as viewed')}
                            />
                          )}
                          <div
                            className={classnames(
                              'w-full overflow-hidden truncate',
                              { 'text-center': index !== 0 }
                            )}
                          >
                            {content}
                          </div>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
              {isExpanded && (
                <tr className="max-h-0 w-full select-text overflow-hidden bg-black">
                  <td colSpan={row.length}>{expandedContent}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
};

StudyListTableRow.propTypes = {
  tableData: PropTypes.shape({
    row: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        content: PropTypes.node,
        title: PropTypes.string,
        gridCol: PropTypes.number.isRequired,
      })
    ).isRequired,
    expandedContent: PropTypes.node.isRequired,
    onClickRow: PropTypes.func.isRequired,
    isExpanded: PropTypes.bool.isRequired,
    dataCY: PropTypes.string,
    studyData: PropTypes.shape({
      isChecked: PropTypes.bool,
    }),
    toggleChecked: PropTypes.func,
  }),
};

const MemoizedStudyListTableRow = memo(StudyListTableRow);

export default MemoizedStudyListTableRow;
export { MemoizedStudyListTableRow as StudyListTableRow };
